import { Injectable } from '@angular/core';
import { Selector } from '@ngxs/store';
import _ from 'lodash';
import { CallTagsState, TagReplaceResult } from 'src/app/store/samwin/call-tags/call-tags.state';
import { SelectedSubscriberState } from 'src/app/store/samwin/directory/selected-subscriber/selected-subscriber.state';
import { SamwinAcdExtensionStateSelectors } from 'src/app/store/samwin/samwin-acd-extension/samwin-acd-extension.selectors';
import { SamwinCallListState } from 'src/app/store/samwin/samwin-call-list/samwin-call-list.state';
import { SamwinLinesStateSelector } from 'src/app/store/samwin/samwin-lines/samwin-lines.selector';
import { SamwinSkillsState } from 'src/app/store/samwin/samwin-skills/samwin-skills.state';
import { SamwinUserStatusState } from 'src/app/store/samwin/samwin-user-status/samwin-user-status.state';
import { SessionState, SessionUserInfo } from 'src/app/store/session/session.state';
import { CallTagsForSingleCallRemoteDto } from 'src/generated/signalR/call-tags-for-single-call-remote-dto';
import { SamwinCallInfoRemoteDto } from 'src/generated/signalR/samwin-call-info-remote-dto';
import { SkillConfigurationRemoteDto } from 'src/generated/signalR/skill-configuration-remote-dto';
import { UserAcdExtensionInfoRemoteDto } from 'src/generated/signalR/user-acd-extension-info-remote-dto';
import { DirectoryEntryRemoteDto, OrganisationEntryRemoteDto } from 'src/generated/swagger';
import { DirectoryStructureSelectors } from '../directory/directory-structure/directory-structure.selectors';

@Injectable()
export class CallTagsSelectors {
   @Selector()
   public static doReplaceTag(): (input: string, tagName: string, tagValue: string, urlEncode: boolean) => string {
      return (input: string, tagName: string, tagValue: string, urlEncode: boolean) => {
         const isUrlTag = tagName.startsWith('URL');
         const valueToUse = urlEncode && !isUrlTag ? encodeURIComponent(tagValue) : tagValue;

         const regExp = new RegExp(`%%${tagName}%%`, 'g');
         return input.replace(regExp, valueToUse);
      };
   }

   @Selector()
   public static getCallTag(): (callTags: CallTagsForSingleCallRemoteDto, tagName: string) => TagReplaceResult {
      return (callTags: CallTagsForSingleCallRemoteDto, tagName: string) => {
         if (_.isNil(tagName) || _.isNil(callTags)) {
            return new TagReplaceResult('', false);
         }
         const tagValue = callTags.callTags[tagName];
         return _.isNil(tagValue) ? new TagReplaceResult('', false) : new TagReplaceResult(tagValue, true);
      };
   }

   @Selector()
   public static insertCleanTagNameAtIndex(): (htmlTags: string, index: number, cleanTagName: string) => string {
      return (htmlTags: string, index: number, cleanTagName: string) => {
         const characters: string[] = htmlTags.split('');
         characters.splice(index, 0, ...cleanTagName.split(''));
         return characters.join('');
      };
   }

   @Selector()
   public static getHtmlTagsContent(): (input: string) => string {
      return (input: string) => {
         const regex: RegExp = /<[^>]+>/g;
         const htmlTags: RegExpMatchArray = input.match(regex);
         return htmlTags.join('');
      };
   }

   @Selector()
   public static getCleanTagName(): (tagName: string) => string {
      return (tagName: string) => {
         const regex: RegExp = /<[^>]*>/g;
         const cleanTagName = tagName.replace(regex, '');
         return `%%${cleanTagName}%%`;
      };
   }

   @Selector()
   public static getTagStartingIndex(): (tag: string) => number {
      return (tag: string) => {
         const regex = /^(?:[^>]*>(.*?)<|([^<]+))/;
         const tagName = regex.exec(tag);

         if (tagName) {
            return tagName.index;
         }

         return -1;
      };
   }

   @Selector()
   public static getTagNames(): (inputText: string) => string[] {
      return (inputText: string) => {
         // @ts-ignore
         const tags: string[] = [...inputText.matchAll(/%%([^%]+)%%/g)];

         return tags.map((match) => match[1]);
      };
   }

   @Selector([SamwinLinesStateSelector.getRingingCallId, SamwinLinesStateSelector.getActiveCallId, SamwinCallListState.previousCallId])
   public static callIdForTag(ringingCallId: number, activeCallId: number, previousCallId: number) {
      if (!_.isNil(ringingCallId)) {
         return ringingCallId;
      }
      if (!_.isNil(activeCallId)) {
         return activeCallId;
      }
      if (!_.isNil(previousCallId)) {
         return previousCallId;
      }
      return null;
   }

   @Selector([CallTagsState.callTagIdsForCalls, CallTagsSelectors.callIdForTag])
   public static lastActiveCallIdWithTag(callTagIdsForCalls: number[], callId: number) {
      if (callTagIdsForCalls.includes(callId)) {
         return callId;
      }
      return null;
   }

   @Selector([CallTagsState.callTagsForCalls, CallTagsSelectors.callIdForTag])
   public static callTagsForCurrentCall(callTags: { [callId: number]: CallTagsForSingleCallRemoteDto }, callId: number) {
      return callTags[callId];
   }

   @Selector([CallTagsSelectors.lastActiveCallIdWithTag])
   public static isTagAvailableForCurrentCall(lastActiveCallId: number) {
      return !_.isNil(lastActiveCallId);
   }

   @Selector([CallTagsSelectors.callTagsForCurrentCall, CallTagsSelectors.getCallTag])
   public static getCallTagByName(
      callTags: CallTagsForSingleCallRemoteDto,
      getCallTag: (callTags: CallTagsForSingleCallRemoteDto, tagName: string) => TagReplaceResult
   ) {
      return (tagName: string) => {
         return getCallTag(callTags, tagName);
      };
   }

   @Selector([CallTagsSelectors.callTagsForActiveOrRingingCall, CallTagsSelectors.getCallTag])
   public static getCallTagFromActiveOrRingingCall(
      callTagsForActiveOrRingingCall,
      getCallTag: (callTags: CallTagsForSingleCallRemoteDto, tagName: string) => TagReplaceResult
   ): (tagName: string) => TagReplaceResult {
      return (tagName: string) => {
         return getCallTag(callTagsForActiveOrRingingCall, tagName);
      };
   }

   @Selector([CallTagsSelectors.getCallTagFromActiveOrRingingCall])
   public static isCallTagSetToValueOnActiveOrRingingCall(
      getCallTagFromActiveOrRingingCall: (tagName: string) => TagReplaceResult
   ): (tagName: string, tagValue: string) => boolean {
      return (tagName, tagValue) => {
         if (_.isNil(tagName) || _.isEmpty(tagName)) {
            return false;
         }
         return getCallTagFromActiveOrRingingCall(tagName).text === tagValue;
      };
   }

   @Selector([CallTagsSelectors.isTagAvailableForCurrentCall, SamwinUserStatusState.isCurrentStatusInfoValid])
   public static isCalltagAndStatusAvailable(isTagAvailableForCurrentCall: boolean, isCurrentStatusInfoValid: boolean): boolean {
      return isTagAvailableForCurrentCall && isCurrentStatusInfoValid;
   }

   @Selector([
      CallTagsSelectors.isCalltagAndStatusAvailable,
      SamwinLinesStateSelector.isCallInProgress,
      SamwinUserStatusState.isUserStateAutoACW,
      SamwinUserStatusState.isUserStateBusy,
   ])
   public static areCallTagsEditable(
      isCalltagAndStatusAvailable: boolean,
      isCallInProgress: boolean,
      isUserStateAutoACW: boolean,
      isUserStateBusy: boolean
   ): boolean {
      if (!isCalltagAndStatusAvailable) {
         return false;
      }
      if (isCallInProgress || isUserStateAutoACW || isUserStateBusy) {
         return true;
      }
      return false;
   }

   @Selector([CallTagsState.callTagsForCalls, CallTagsSelectors.callIdForTag])
   public static currentCalltags(callTags: { [callId: number]: CallTagsForSingleCallRemoteDto }, callIdForTag: number) {
      return callTags[callIdForTag];
   }

   @Selector([
      CallTagsSelectors.currentCalltags,
      CallTagsSelectors.isCalltagAndStatusAvailable,
      SamwinLinesStateSelector.isPhoneRingingOrInProgressOrAnswering,
      SamwinUserStatusState.isUserStateAutoACW,
      SamwinUserStatusState.isUserStateBusy,
   ])
   public static callTagsForActiveOrRingingCall(
      currentCalltags: CallTagsForSingleCallRemoteDto,
      isCalltagAndStatusAvailable: boolean,
      isPhoneRingingOrInProgressOrAnswering: boolean,
      isUserStateAutoACW: boolean,
      isUserStateBusy: boolean
   ): CallTagsForSingleCallRemoteDto {
      if (!isCalltagAndStatusAvailable) {
         return null;
      }
      if (isPhoneRingingOrInProgressOrAnswering || isUserStateAutoACW || isUserStateBusy) {
         return currentCalltags;
      }
      return null;
   }

   private static areTagsRemaining(tag: TagReplaceResult): boolean {
      const tagRegExp = /%%[^%]+%%/g;

      if (tagRegExp.test(tag.text)) {
         return true;
      } else {
         return false;
      }
   }

   @Selector([
      // Note: Call Tags Selector updates the stream multiple times with the same value
      CallTagsSelectors.callTagsForActiveOrRingingCall,
      CallTagsSelectors.getReplaceAllTagsInText,
      SamwinCallListState.getCallInfoFromPreviousCall,
      SelectedSubscriberState.selectedSubscriber,
   ])
   public static getReplaceAllTagsInTextForActiveOrRingingCallOrAfterCallWorkFn(
      callTags: CallTagsForSingleCallRemoteDto,
      getReplaceAllTagsInText,
      callerInfo: SamwinCallInfoRemoteDto,
      selectedSubscriber: DirectoryEntryRemoteDto
   ) {
      return (inputText: string, urlEncode: boolean = false) => {
         let result: TagReplaceResult;
         result = getReplaceAllTagsInText(inputText, callTags, callerInfo, selectedSubscriber, urlEncode);

         while (this.areTagsRemaining(result)) {
            result = getReplaceAllTagsInText(result.text, callTags, callerInfo, selectedSubscriber, urlEncode);
         }

         return result;
      };
   }

   @Selector([
      SessionState.getAuthenticatedUser,
      SamwinAcdExtensionStateSelectors.getCurrentAcdExtension,
      CallTagsSelectors.replaceUserInfoTags,
      CallTagsSelectors.replaceCallerInfoTags,
      CallTagsSelectors.replaceSubscriberTags,
      CallTagsSelectors.replaceAcdExtensionTags,
      CallTagsSelectors.getReplacedText,
      CallTagsSelectors.cleanInputText,
      CallTagsSelectors.getTagNames,
   ])
   public static getReplaceAllTagsInText(
      userInfo: SessionUserInfo,
      acdExtension: UserAcdExtensionInfoRemoteDto,
      replaceUserInfoTags: (inputText: string, userInfo: SessionUserInfo, urlEncode: boolean) => string,
      replaceCallerInfoTags: (inputText: string, callerInfo: SamwinCallInfoRemoteDto, urlEncode: boolean) => string,
      replaceSubscriberTags: (inputText: string, selectedSubscriber: DirectoryEntryRemoteDto, urlEncode: boolean) => string,
      replaceAcdExtensionTags: (inputText: string, acdExtension: UserAcdExtensionInfoRemoteDto, urlEncode: boolean) => string,
      getReplacedText: (tagNames: string[], callTags: CallTagsForSingleCallRemoteDto, inputText: string, urlEncode: boolean) => TagReplaceResult,
      cleanInputText: (tagNames: string[], inputText: string) => string,
      getTagNames: (inputText: string) => string[]
   ) {
      return (
         inputText: string,
         callTags: CallTagsForSingleCallRemoteDto,
         callerInfo: SamwinCallInfoRemoteDto,
         selectedSubscriber: DirectoryEntryRemoteDto,
         urlEncode: boolean = false
      ) => {
         if (_.isEmpty(inputText) || _.isNil(inputText)) {
            return new TagReplaceResult('', true);
         }

         let tagNames: string[] = getTagNames(inputText);

         inputText = cleanInputText(tagNames, inputText);

         if (userInfo) {
            // User Info
            inputText = replaceUserInfoTags(inputText, userInfo, urlEncode);
         }
         if (callerInfo) {
            // Caller Info
            inputText = replaceCallerInfoTags(inputText, callerInfo, urlEncode);
         }
         if (selectedSubscriber) {
            inputText = replaceSubscriberTags(inputText, selectedSubscriber, urlEncode);
         }
         if (acdExtension) {
            inputText = replaceAcdExtensionTags(inputText, acdExtension, urlEncode);
         }

         tagNames = getTagNames(inputText);

         if (_.isEmpty(tagNames)) {
            return new TagReplaceResult(inputText, true);
         }

         return getReplacedText(tagNames, callTags, inputText, urlEncode);
      };
   }

   @Selector([
      CallTagsSelectors.insertCleanTagNameAtIndex,
      CallTagsSelectors.getCleanTagName,
      CallTagsSelectors.getHtmlTagsContent,
      CallTagsSelectors.getTagStartingIndex,
   ])
   public static cleanInputText(
      insertCleanTagNameAtIndex: (inputText: string, index: number, cleanTagName: string) => string,
      getCleanTagName: (tagValue: string) => string,
      getHtmlTagsContent: (input: string) => string,
      getTagStartingIndex: (input: string) => number
   ) {
      return (tagNames: string[], inputText: string) => {
         tagNames.forEach((tagName) => {
            const htmlTagRegex: RegExp = /<[^>]+>/g;
            const containsHtmlTags: boolean = htmlTagRegex.test(tagName);
            if (containsHtmlTags) {
               const cleanTagName: string = getCleanTagName(tagName);
               const tagStartingIndex: number = getTagStartingIndex(tagName);
               const htmlTags: string = getHtmlTagsContent(tagName);
               const result: string = insertCleanTagNameAtIndex(htmlTags, tagStartingIndex, cleanTagName);

               inputText = inputText.replace(`%%${tagName}%%`, result);
            }
         });
         return inputText;
      };
   }

   @Selector([CallTagsSelectors.doReplaceTag])
   public static replaceUserInfoTags(doReplaceTag: (input: string, tagName: string, tagValue: string, urlEncode: boolean) => string) {
      return (inputText: string, userInfo: SessionUserInfo, urlEncode: boolean) => {
         inputText = doReplaceTag(inputText, 'USERNAME', userInfo?.userName ?? '', urlEncode);
         inputText = doReplaceTag(inputText, 'USERREALNAME', userInfo?.displayName ?? '', urlEncode);
         inputText = doReplaceTag(inputText, 'USERFIRSTNAME', userInfo?.firstname ?? '', urlEncode);
         inputText = doReplaceTag(inputText, 'USERLASTNAME', userInfo?.lastname ?? '', urlEncode);
         inputText = doReplaceTag(inputText, 'USERMAIL', userInfo?.email ?? '', urlEncode);
         return inputText;
      };
   }

   @Selector([CallTagsSelectors.doReplaceTag, SamwinSkillsState.getSkillConfigurationFn])
   public static replaceCallerInfoTags(
      doReplaceTag: (input: string, tagName: string, tagValue: string, urlEncode: boolean) => string,
      getSkillShortInfo: (skillId: number) => SkillConfigurationRemoteDto
   ) {
      return (inputText: string, callerInfo: SamwinCallInfoRemoteDto, urlEncode: boolean) => {
         inputText = doReplaceTag(inputText, 'ID', callerInfo?.callId?.toString() ?? '', urlEncode);
         inputText = doReplaceTag(inputText, 'CALLERNAME', callerInfo?.peerName ?? '', urlEncode);
         inputText = doReplaceTag(inputText, 'CALLERID', callerInfo?.peerId ?? '', urlEncode);
         inputText = doReplaceTag(inputText, 'SKILLMAIL', getSkillShortInfo(callerInfo?.queueId)?.skillEmail ?? '%%SKILLMAIL%%', urlEncode);
         return inputText;
      };
   }

   @Selector([CallTagsSelectors.doReplaceTag, CallTagsSelectors.replaceAdditionalTags])
   public static replaceSubscriberTags(
      doReplaceTag: (input: string, tagName: string, tagValue: string, urlEncode: boolean) => string,
      replaceAdditionalTags: (inputText: string, selectedSubscriber: DirectoryEntryRemoteDto, urlEncode: boolean) => string
   ) {
      return (inputText: string, selectedSubscriber: DirectoryEntryRemoteDto, urlEncode: boolean) => {
         inputText = doReplaceTag(inputText, 'SELECTED_SUBSCRIBERID', selectedSubscriber?.id?.toString() ?? '', urlEncode);
         inputText = doReplaceTag(inputText, 'SELECTED_SUBSCRIBER', selectedSubscriber?.orgnam ?? '', urlEncode);
         inputText = doReplaceTag(inputText, 'SELECTED_SUBSCRIBERNAME', selectedSubscriber?.orgnam ?? '', urlEncode);
         inputText = doReplaceTag(inputText, 'SELECTED_SUBSCRIBERMAIL', selectedSubscriber?.email?.value ?? '', urlEncode);
         inputText = doReplaceTag(inputText, 'SELECTED_SUBSCRIBERNUMBER', selectedSubscriber?.orgnum ?? '', urlEncode);
         inputText = doReplaceTag(inputText, 'SELECTED_SUBSCRIBERMOBILE', selectedSubscriber?.fldMobile ?? '', urlEncode);
         inputText = replaceAdditionalTags(inputText, selectedSubscriber, urlEncode);

         return inputText;
      };
   }

   @Selector([CallTagsSelectors.doReplaceTag, DirectoryStructureSelectors.getOrganisationDataEntryForDirectoryColumn])
   public static replaceAdditionalTags(
      doReplaceTag: (input: string, tagName: string, tagValue: string, urlEncode: boolean) => string,
      getOrganisationDataEntryForDirectoryColumn: (selectedSubscriber: DirectoryEntryRemoteDto, organisationUnitNumber: string) => OrganisationEntryRemoteDto
   ) {
      return (inputText: string, selectedSubscriber: DirectoryEntryRemoteDto, urlEncode: boolean) => {
         const orgFieldRegex: RegExp = new RegExp(/TBL_ORG(\d{2})\.FLD_([^%]+)/gi);
         let match: RegExpExecArray = orgFieldRegex.exec(inputText);

         while (match) {
            const tag: string = match[0];
            const orgId: string = match[1];
            const field: string = match[2].toLowerCase();
            const orgEntry: OrganisationEntryRemoteDto = getOrganisationDataEntryForDirectoryColumn(selectedSubscriber, orgId);

            if (orgEntry) {
               if (field.startsWith('orgfeld')) {
                  const orgfeldId: string = field.substring(7);
                  const fieldValue: string = orgEntry[`field${orgfeldId}`]?.value ?? '';

                  inputText = doReplaceTag(inputText, tag, fieldValue, urlEncode);
               } else {
                  const fieldValue: string = typeof orgEntry[field] === 'string' ? orgEntry[field] : orgEntry[field]?.value || '';

                  inputText = doReplaceTag(inputText, tag, fieldValue, urlEncode);
               }
            }

            match = orgFieldRegex.exec(inputText);
         }
         return inputText;
      };
   }

   @Selector([CallTagsSelectors.doReplaceTag, SamwinSkillsState.getSkillConfigurationFn])
   public static replaceAcdExtensionTags(
      doReplaceTag: (input: string, tagName: string, tagValue: string, urlEncode: boolean) => string,
      getSkillShortInfo: (skillId: number) => SkillConfigurationRemoteDto
   ) {
      return (inputText: string, acdExtension: UserAcdExtensionInfoRemoteDto, urlEncode: boolean) => {
         // ACD-Extension
         inputText = doReplaceTag(inputText, 'ACD_EXTENSION', acdExtension?.name ?? '', urlEncode);
         // Default Skill E-Mail
         inputText = doReplaceTag(inputText, 'SKILLMAIL', getSkillShortInfo(acdExtension?.defaultSkillId)?.skillEmail ?? '', urlEncode);
         return inputText;
      };
   }

   @Selector([CallTagsSelectors.doReplaceTag, CallTagsSelectors.getCallTag])
   public static getReplacedText(
      doReplaceTag: (input: string, tagName: string, tagValue: string, urlEncode: boolean) => string,
      getCallTag: (callTags: CallTagsForSingleCallRemoteDto, tagName: string) => TagReplaceResult
   ) {
      return (tagNames: string[], callTags: CallTagsForSingleCallRemoteDto, inputText: string, urlEncode: boolean) => {
         if (_.isEmpty(tagNames)) {
            return new TagReplaceResult(inputText, true);
         }

         let allTagsContainValue = true;
         _(tagNames)
            .uniq()
            .map((tagName) => {
               const result = getCallTag(callTags, tagName);
               allTagsContainValue = allTagsContainValue && result.allReplacedTagsFound;
               inputText = doReplaceTag(inputText, tagName, result.text, urlEncode);
            })
            .value();

         return new TagReplaceResult(inputText, allTagsContainValue);
      };
   }
}
